import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { supabase } from "./supabase";
import { PublicTracker } from "./PublicTracker";

interface TimeLeft {
  DAYS: number;
  HRS: number;
  MINS: number;
  SECS: number;
}

const LEAGUE_LOGO_URL =
  "https://dpsrbqvcukoomuupjfdm.supabase.co/storage/v1/object/public/draft%20bucket/Empire%20League%20Logo.png";

export const AtmosphericCountdown: React.FC = () => {
  const [sessions, setSessions] = useState<any[]>([]);
  const [activeSession, setActiveSession] = useState<any>(null);
  const [timeLeft, setTimeLeft] = useState<TimeLeft>({
    DAYS: 0,
    HRS: 0,
    MINS: 0,
    SECS: 0,
  });
  const [cinematicMode, setCinematicMode] = useState<"start" | "end" | null>(
    null
  );
  const [showTracker, setShowTracker] = useState(false);

  const refreshData = async () => {
    const { data } = await supabase
      .from("draft_sessions")
      .select("*")
      .order("year", { ascending: false });

    if (data && data.length > 0) {
      setSessions(data);

      if (!activeSession) {
        const current = data.find((s: any) => s.is_current) || data[0];
        setActiveSession(current);
        if (current.status !== "pre-draft") {
          setShowTracker(true);
        }
      } else {
        const updated = data.find((s: any) => s.id === activeSession.id);
        if (updated) setActiveSession(updated);
      }
    }
  };

  useEffect(() => {
    refreshData();
    const channel = supabase
      .channel("draft-updates")
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "draft_sessions" },
        (payload: any) => {
          if (activeSession && payload.new.id === activeSession.id) {
            if (
              payload.new.status === "live" &&
              payload.old.status !== "live"
            ) {
              setCinematicMode("start");
            } else if (
              payload.new.status === "concluded" &&
              payload.old.status !== "concluded"
            ) {
              setCinematicMode("end");
            }
          }
          refreshData();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [activeSession?.id]);

  useEffect(() => {
    if (cinematicMode) {
      const timer = setTimeout(() => {
        setShowTracker(true);
        setCinematicMode(null);
      }, 6000);
      return () => clearTimeout(timer);
    }
  }, [cinematicMode]);

  useEffect(() => {
    const timer = setInterval(() => {
      if (!activeSession?.scheduled_start) return;
      const target = new Date(activeSession.scheduled_start).getTime();
      const diff = target - new Date().getTime();
      if (diff > 0) {
        setTimeLeft({
          DAYS: Math.floor(diff / 86400000),
          HRS: Math.floor((diff / 3600000) % 24),
          MINS: Math.floor((diff / 60000) % 60),
          SECS: Math.floor((diff / 1000) % 60),
        });
      } else {
        setTimeLeft({ DAYS: 0, HRS: 0, MINS: 0, SECS: 0 });
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [activeSession]);

  if (showTracker && !cinematicMode) {
    return <PublicTracker initialSessionId={activeSession?.id} />;
  }

  return (
    <div className="countdown-scene">
      <div className="sky-gradient">
        <div className="central-glow" />
      </div>
      <div className="palm-container">
        <img
          src="https://www.transparentpng.com/download/palm-tree/X6XG7m-palm-tree-silhouette-clipart-transparent.png"
          className="palm palm-left"
          alt=""
        />
        <img
          src="https://www.transparentpng.com/download/palm-tree/X6XG7m-palm-tree-silhouette-clipart-transparent.png"
          className="palm palm-right"
          alt=""
        />
      </div>

      <AnimatePresence mode="wait">
        {cinematicMode ? (
          <motion.div
            key="cinematic"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, filter: "blur(20px)" }}
            transition={{ duration: 1 }}
            className="content-box"
          >
            <motion.img
              animate={{
                filter: [
                  "drop-shadow(0 0 20px #22565a)",
                  "drop-shadow(0 0 50px #22565a)",
                  "drop-shadow(0 0 20px #22565a)",
                ],
              }}
              transition={{ duration: 3, repeat: Infinity }}
              src={LEAGUE_LOGO_URL}
              className="logo-cinematic-img"
              alt="Empire Logo"
            />
            <h1 className="cinematic-title">EMPIRE LEAGUE DRAFT</h1>
            <h2 className="cinematic-subtitle">
              {cinematicMode === "start"
                ? "NOW IN SESSION"
                : "HAS NOW CONCLUDED"}
            </h2>
          </motion.div>
        ) : (
          <motion.div
            key="countdown"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="content-box"
          >
            <img
              src={LEAGUE_LOGO_URL}
              className="logo-header-img"
              alt="Empire Logo"
            />
            <div className="league-tag">EMPIRE LEAGUE DRAFT STARTS IN</div>
            <div className="timer-main">
              {(["DAYS", "HRS", "MINS", "SECS"] as const).map((label) => (
                <div key={label} className="unit-container">
                  <div className="unit-value">
                    <AnimatePresence mode="popLayout">
                      <motion.span
                        key={`${label}-${timeLeft[label]}`}
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: -20, opacity: 0 }}
                      >
                        {timeLeft[label] < 10
                          ? `0${timeLeft[label]}`
                          : timeLeft[label]}
                      </motion.span>
                    </AnimatePresence>
                  </div>
                  <span className="unit-label">{label}</span>
                </div>
              ))}
            </div>
            <div className="selector-wrapper">
              <span className="selector-label">SELECT SEASON:</span>
              <select
                className="session-dropdown"
                value={activeSession?.id || ""}
                onChange={(e) => {
                  const selected = sessions.find(
                    (s) => s.id === e.target.value
                  );
                  if (selected) {
                    setActiveSession(selected);
                    if (selected.status !== "pre-draft") setShowTracker(true);
                    else setShowTracker(false);
                  }
                }}
              >
                {sessions.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.year} {s.status.toUpperCase()}
                  </option>
                ))}
              </select>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`
        .countdown-scene { position: fixed; inset: 0; background: #000; display: flex; align-items: center; justify-content: center; overflow: hidden; z-index: 99; font-family: 'Oswald', sans-serif; }
        .sky-gradient { position: absolute; inset: 0; background: radial-gradient(circle at 50% 50%, #112e31 0%, #000 100%); }
        .central-glow { position: absolute; width: 100vw; height: 100vh; background: radial-gradient(circle, rgba(34, 86, 90, 0.15) 0%, transparent 70%); filter: blur(100px); }
        .palm-container { position: absolute; inset: 0; pointer-events: none; z-index: 50; }
        .palm { position: absolute; bottom: -80px; height: 110%; filter: brightness(0); opacity: 0.15; }
        .palm-left { left: -180px; transform: scaleX(-1); }
        .palm-right { right: -180px; }
        .content-box { position: relative; z-index: 100; text-align: center; display: flex; flex-direction: column; align-items: center; }
        .cinematic-title { color: #f9eeda; font-size: 5.5rem; font-weight: 900; margin: 0; text-transform: uppercase; letter-spacing: -2px; line-height: 0.9; }
        .cinematic-subtitle { color: #22565a; font-size: 1.5rem; letter-spacing: 12px; text-transform: uppercase; margin-top: 20px; }
        .logo-cinematic-img { height: 180px; margin-bottom: 30px; }
        .logo-header-img { height: 100px; margin-bottom: 20px; }
        .league-tag { color: #22565a; font-weight: 700; letter-spacing: 8px; margin-bottom: 40px; text-transform: uppercase; }
        .timer-main { display: flex; gap: 40px; margin-bottom: 50px; }
        .unit-container { display: flex; flex-direction: column; align-items: center; min-width: 120px; }
        .unit-value { font-size: 8rem; font-weight: 900; color: #f9eeda; line-height: 1; height: 8rem; display: flex; align-items: center; text-shadow: 0 10px 40px rgba(0,0,0,0.8); font-variant-numeric: tabular-nums; }
        .unit-label { color: #22565a; font-weight: 600; letter-spacing: 4px; margin-top: 10px; text-transform: uppercase; }
        .selector-wrapper { display: inline-flex; align-items: center; gap: 12px; background: rgba(34, 86, 90, 0.1); padding: 8px 20px; border-radius: 50px; border: 1px solid rgba(34, 86, 90, 0.3); backdrop-filter: blur(10px); }
        .selector-label { color: #22565a; font-size: 0.75rem; font-weight: 700; letter-spacing: 2px; }
        .session-dropdown { background: transparent; border: none; color: #f9eeda; font-family: 'Oswald', sans-serif; font-size: 1.1rem; font-weight: 700; cursor: pointer; outline: none; text-transform: uppercase; }
        @media (max-width: 768px) { .timer-main { gap: 10px; } .unit-value { font-size: 3.5rem; height: 3.5rem; } .unit-container { min-width: 70px; } .cinematic-title { font-size: 2.8rem; } .logo-cinematic-img { height: 120px; } }
      `}</style>
    </div>
  );
};
